/**
 * CrearPlazoFijoModal — modal para que el operador cree un PF directamente
 * en el admin. Caso típico: el asociado deposita por caja/transferencia y
 * pide asistencia. El operador busca al asociado, llena los términos,
 * confirma → RPC `crear_plazo_fijo_admin` genera todo atómicamente.
 *
 * V1 simple: monto + plazo + manejo + modalidad renovación + origen +
 * cuenta de acreditación si manejo es MENSUAL. Override de tasa y
 * beneficiarios quedan para V2.
 */
import { useEffect, useMemo, useState } from 'react'
import { confirmarClave, usePuedeTasaPreferencial } from '@/lib/tasaPreferencial'
import {
  X,
  MagnifyingGlass as Search,
  CheckCircle,
  ArrowRight,
  Sparkle,
  Plus,
  Trash,
  Warning,
} from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'
import { hoyGT } from '../lib/fechas'

type Asociado = {
  id: string
  no_asociado: string
  nombre_completo: string
  dpi: string
  cuenta_ahorro: string | null
}

type Beneficiario = {
  nombre_completo: string
  identificacion: string
  parentesco: string
  porcentaje: number
}

const BENEFICIARIO_VACIO: Beneficiario = {
  nombre_completo: '',
  identificacion: '',
  parentesco: '',
  porcentaje: 0,
}

const PLAZO_OPCIONES = [
  { dias: 180, label: '6 meses' },
  { dias: 365, label: '1 año' },
  { dias: 545, label: '1.5 años' },
  { dias: 730, label: '2 años' },
] as const

export function CrearPlazoFijoModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (resultado: { plazo_fijo_id: string; certificado_no: string }) => void
}) {
  const [asociado, setAsociado] = useState<Asociado | null>(null)
  const [searchQ, setSearchQ] = useState('')
  const [searchResults, setSearchResults] = useState<Asociado[]>([])
  const [searching, setSearching] = useState(false)

  const [montoStr, setMontoStr] = useState('')
  const [plazo, setPlazo] = useState<180 | 365 | 545 | 730>(365)
  const [manejo, setManejo] = useState<'MENSUAL' | 'AL_VENCIMIENTO'>('MENSUAL')
  const [renovacion, setRenovacion] = useState<'AUTOMATICA' | 'MANUAL'>('AUTOMATICA')
  const [origen, setOrigen] = useState<'CAJA' | 'DEBITO_AHORRO_CORRIENTE'>('CAJA')
  const [beneficiarios, setBeneficiarios] = useState<Beneficiario[]>([])
  // Tasa: 12% estándar · otra tasa = preferencial (requiere motivo, queda autorizada por el usuario)
  const [tasaStr, setTasaStr] = useState('12')
  const [motivoTasa, setMotivoTasa] = useState('')
  const [claveTasa, setClaveTasa] = useState('')
  const puedeTasa = usePuedeTasaPreferencial(open)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [resultado, setResultado] = useState<{
    plazo_fijo_id: string
    certificado_no: string
    cuenta_id: string
    capital: number
    fecha_vencimiento: string
    total_neto: number
  } | null>(null)

  // Reset al cerrar/abrir
  useEffect(() => {
    if (!open) {
      setAsociado(null)
      setSearchQ('')
      setSearchResults([])
      setMontoStr('')
      setPlazo(365)
      setManejo('MENSUAL')
      setRenovacion('AUTOMATICA')
      setOrigen('CAJA')
      setBeneficiarios([])
      setTasaStr('12')
      setMotivoTasa('')
      setClaveTasa('')
      setError(null)
      setResultado(null)
    }
  }, [open])

  const sumaPorcentajes = beneficiarios.reduce(
    (s, b) => s + (Number.isFinite(b.porcentaje) ? b.porcentaje : 0),
    0,
  )
  const beneficiariosValidos =
    beneficiarios.length === 0 ||
    (sumaPorcentajes === 100 &&
      beneficiarios.every(
        (b) =>
          b.nombre_completo.trim() &&
          b.identificacion.trim() &&
          b.parentesco.trim() &&
          b.porcentaje > 0,
      ))

  const monto = useMemo(() => {
    const clean = montoStr.replace(/[^\d.]/g, '')
    const n = parseFloat(clean)
    return isNaN(n) ? 0 : n
  }, [montoStr])

  const tasaPct = Number(tasaStr.replace(',', '.'))
  const tasaValida = Number.isFinite(tasaPct) && tasaPct > 0 && tasaPct <= 20
  const esPreferencial = tasaValida && Math.abs(tasaPct - 12) > 1e-9

  const canSubmit =
    !!asociado &&
    tasaValida &&
    (!esPreferencial || (puedeTasa && motivoTasa.trim().length >= 5 && claveTasa.length > 0)) &&
    monto >= 1000 &&
    (manejo === 'AL_VENCIMIENTO' || !!asociado.cuenta_ahorro) &&
    beneficiariosValidos

  // Buscar asociado por DPI/no_asociado/nombre
  useEffect(() => {
    if (asociado) return
    const q = searchQ.trim()
    if (q.length < 3) {
      setSearchResults([])
      return
    }
    let cancelled = false
    setSearching(true)
    const t = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('asociado')
          .select('id, no_asociado, nombre_completo, dpi')
          .or(`dpi.ilike.%${q}%,no_asociado.ilike.%${q}%,nombre_completo.ilike.%${q}%`)
          .eq('estado_asociado', 'activo')
          .limit(8)

        if (cancelled) return
        const list = (data ?? []) as Omit<Asociado, 'cuenta_ahorro'>[]

        // Resolve cuenta_ahorro de cada uno
        const ids = list.map((a) => a.id)
        let ahorroByAsociado = new Map<string, string>()
        if (ids.length > 0) {
          const { data: cuentas } = await supabase
            .from('cuenta')
            .select('asociado_id, cuenta_id, tipo_servicio')
            .in('asociado_id', ids)
            .eq('tipo_servicio', 'AHORRO CORRIENTE')
          ;(cuentas ?? []).forEach((c: any) => {
            if (!ahorroByAsociado.has(c.asociado_id)) {
              ahorroByAsociado.set(c.asociado_id, c.cuenta_id)
            }
          })
        }

        setSearchResults(
          list.map((a) => ({
            ...a,
            cuenta_ahorro: ahorroByAsociado.get(a.id) ?? null,
          })),
        )
      } finally {
        if (!cancelled) setSearching(false)
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(t)
    }
  }, [searchQ, asociado])

  const onSubmit = async () => {
    if (!asociado || !canSubmit) return
    setSubmitting(true)
    setError(null)
    try {
      const beneficiariosPayload = beneficiarios.map((b, i) => ({
        nombre_completo: b.nombre_completo.trim(),
        identificacion: b.identificacion.trim(),
        parentesco: b.parentesco.trim(),
        porcentaje: b.porcentaje,
        orden: i + 1,
      }))
      if (esPreferencial) {
        const errClave = await confirmarClave(claveTasa)
        if (errClave) {
          setError(errClave)
          return
        }
      }
      const { data, error: rpcErr } = await supabase.rpc('crear_plazo_fijo_admin', {
        p_asociado_id: asociado.id,
        p_capital: monto,
        p_plazo_dias: plazo,
        p_tasa_anual: Math.round(tasaPct * 100) / 10000,
        p_manejo_capitalizacion: manejo,
        p_modalidad_renovacion: renovacion,
        p_origen_fondos: origen,
        p_cuenta_acreditacion_id: manejo === 'MENSUAL' ? asociado.cuenta_ahorro : null,
        p_tasa_override: esPreferencial,
        p_tasa_override_motivo: esPreferencial ? motivoTasa.trim() : null,
        p_beneficiarios: beneficiariosPayload,
        p_fecha_inicio: hoyGT(),
      })

      if (rpcErr) {
        setError(rpcErr.message)
        return
      }

      setResultado({
        plazo_fijo_id: data.plazo_fijo_id,
        certificado_no: data.certificado_no,
        cuenta_id: data.cuenta_id,
        capital: Number(data.capital),
        fecha_vencimiento: data.fecha_vencimiento,
        total_neto: Number(data.total_neto),
      })
    } catch (e: any) {
      setError(e?.message ?? 'No pudimos crear el plazo fijo.')
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) return null

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(17,17,17,0.45)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: '90vh',
          background: '#FFFFFF',
          borderRadius: 18,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 20px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                color: 'var(--brand-500)',
                fontWeight: 700,
                letterSpacing: 0.4,
                textTransform: 'uppercase',
              }}
            >
              Plazo fijo
            </div>
            <div style={{ fontSize: 18, fontWeight: 700, marginTop: 2 }}>
              {resultado ? '¡Plazo fijo creado!' : 'Nuevo plazo fijo'}
            </div>
          </div>
          <button
            onClick={onClose}
            className="btn btn-ghost"
            style={{ width: 36, height: 36, padding: 0 }}
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {resultado ? (
            <ResultadoCard
              resultado={resultado}
              onDone={() =>
                onCreated({
                  plazo_fijo_id: '',
                  certificado_no: resultado.certificado_no,
                })
              }
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* 1. Asociado */}
              <Section title="1. Asociado">
                {asociado ? (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: 12,
                      borderRadius: 10,
                      background: 'rgba(15,138,87,0.08)',
                      border: '1px solid rgba(15,138,87,0.20)',
                    }}
                  >
                    <CheckCircle size={20} weight="duotone" color="#0F8A57" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{asociado.nombre_completo}</div>
                      <div style={{ fontSize: 12, color: 'var(--n-600)', marginTop: 2 }}>
                        {asociado.no_asociado} · DPI {asociado.dpi}
                        {asociado.cuenta_ahorro ? (
                          <>
                            {' · '}
                            <span style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>
                              {asociado.cuenta_ahorro}
                            </span>
                          </>
                        ) : (
                          <span style={{ color: '#A32D2D' }}>
                            {' · '}sin cuenta de ahorro corriente
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => setAsociado(null)}
                      className="btn btn-ghost"
                      style={{ fontSize: 12 }}
                    >
                      Cambiar
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="search-input-wrap">
                      <Search size={14} weight="bold" className="search-icon" />
                      <input
                        autoFocus
                        placeholder="Buscar por DPI, no. asociado o nombre…"
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                      />
                    </div>
                    {searchQ.length >= 3 ? (
                      <div
                        style={{
                          marginTop: 8,
                          maxHeight: 200,
                          overflowY: 'auto',
                          borderRadius: 10,
                          border: '1px solid var(--border)',
                        }}
                      >
                        {searching ? (
                          <div style={{ padding: 14, color: 'var(--n-500)', fontSize: 12 }}>
                            Buscando…
                          </div>
                        ) : searchResults.length === 0 ? (
                          <div style={{ padding: 14, color: 'var(--n-500)', fontSize: 12 }}>
                            Sin resultados.
                          </div>
                        ) : (
                          searchResults.map((a) => (
                            <button
                              key={a.id}
                              onClick={() => setAsociado(a)}
                              style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: '10px 14px',
                                background: 'transparent',
                                border: 'none',
                                borderBottom: '1px solid var(--border)',
                                cursor: 'pointer',
                              }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 500 }}>
                                {a.nombre_completo}
                              </div>
                              <div
                                style={{
                                  fontSize: 11,
                                  color: 'var(--n-500)',
                                  marginTop: 2,
                                }}
                              >
                                {a.no_asociado} · {a.dpi}
                              </div>
                            </button>
                          ))
                        )}
                      </div>
                    ) : null}
                  </>
                )}
              </Section>

              {asociado ? (
                <>
                  <Section title="2. Monto">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 6,
                        paddingBottom: 8,
                        borderBottom: '1px solid var(--border)',
                      }}
                    >
                      <span style={{ fontSize: 22, color: 'var(--n-500)' }}>Q</span>
                      <input
                        value={montoStr}
                        onChange={(e) => setMontoStr(e.target.value)}
                        onBlur={() => {
                          // Al perder foco mostramos "10,000.00" para que Hugo
                          // deje de ver "Q 10000.00" sin separadores.
                          if (monto > 0) {
                            setMontoStr(
                              monto.toLocaleString('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }),
                            )
                          }
                        }}
                        onFocus={() => {
                          // Al recibir foco lo limpiamos al número plano para
                          // edición cómoda con teclado numérico.
                          if (monto > 0) setMontoStr(String(monto))
                        }}
                        placeholder="1,000"
                        inputMode="decimal"
                        style={{
                          flex: 1,
                          fontSize: 26,
                          fontWeight: 700,
                          color: 'var(--n-900)',
                          padding: 0,
                          margin: 0,
                          border: 'none',
                          outline: 'none',
                          fontVariantNumeric: 'tabular-nums',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 6 }}>
                      Mínimo Q 1,000.00 · sin máximo
                    </div>
                  </Section>

                  <Section title="3. Plazo">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {PLAZO_OPCIONES.map((p) => (
                        <button
                          key={p.dias}
                          onClick={() => setPlazo(p.dias as any)}
                          className={
                            'btn ' + (plazo === p.dias ? 'btn-primary' : 'btn-ghost')
                          }
                          style={{ minWidth: 100 }}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                  </Section>

                  <Section title="3b. Tasa anual">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <input
                        value={tasaStr}
                        disabled={!puedeTasa}
                        title={puedeTasa ? undefined : 'Solo el administrador autorizado puede cambiar la tasa'}
                        onChange={(e) => setTasaStr(e.target.value.replace(/[^\d.,]/g, ''))}
                        inputMode="decimal"
                        style={{ width: 90, fontSize: 18, fontWeight: 700, padding: '6px 10px', border: '1px solid var(--border)', borderRadius: 8, fontVariantNumeric: 'tabular-nums' }}
                      />
                      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--n-700)' }}>%</span>
                      {esPreferencial ? (
                        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: 'rgba(255,91,4,0.12)', color: '#C2410C' }}>
                          Tasa preferencial
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: 'var(--n-500)' }}>
                          Estándar 12%{puedeTasa ? '' : ' · 🔒 solo el administrador autorizado puede cambiarla'}
                        </span>
                      )}
                    </div>
                    {!tasaValida ? (
                      <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 6 }}>Tasa inválida (entre 0 y 20%).</div>
                    ) : null}
                    {esPreferencial ? (
                      <div style={{ marginTop: 10 }}>
                        <textarea
                          value={motivoTasa}
                          onChange={(e) => setMotivoTasa(e.target.value)}
                          rows={2}
                          placeholder="Motivo / autorización. Ej.: Tasa preferencial 13% autorizada por Hugo Duarte, negociación 28-oct-2026."
                          style={{ width: '100%', fontSize: 12.5, padding: 8, border: '1px solid var(--border)', borderRadius: 8, resize: 'vertical', fontFamily: 'inherit' }}
                        />
                        <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 4 }}>
                          Obligatorio. Queda en el PF y en el certificado como tasa especial autorizada por tu usuario.
                        </div>
                        <input
                          type="password"
                          value={claveTasa}
                          onChange={(e) => setClaveTasa(e.target.value)}
                          placeholder="Tu clave de NeoCore para autorizar"
                          autoComplete="current-password"
                          style={{ width: '100%', marginTop: 10, fontSize: 13, padding: 8, border: '1px solid var(--border)', borderRadius: 8 }}
                        />
                      </div>
                    ) : null}
                  </Section>

                  <Section title="4. Capitalización">
                    <RadioRow
                      label="Mensual"
                      hint="Los intereses se acreditan cada mes a Ahorro corriente."
                      active={manejo === 'MENSUAL'}
                      onClick={() => setManejo('MENSUAL')}
                    />
                    <RadioRow
                      label="Al vencimiento"
                      hint="Capital + intereses se acreditan juntos al final del plazo."
                      active={manejo === 'AL_VENCIMIENTO'}
                      onClick={() => setManejo('AL_VENCIMIENTO')}
                    />
                  </Section>

                  <Section title="5. Renovación al vencer">
                    <RadioRow
                      label="Automática"
                      hint="Después de 5 días hábiles se renueva con las mismas condiciones."
                      active={renovacion === 'AUTOMATICA'}
                      onClick={() => setRenovacion('AUTOMATICA')}
                    />
                    <RadioRow
                      label="Manual"
                      hint="Al vencer, el asociado decide qué hacer."
                      active={renovacion === 'MANUAL'}
                      onClick={() => setRenovacion('MANUAL')}
                    />
                  </Section>

                  <Section title="6. Origen de los fondos">
                    <RadioRow
                      label="Depósito en caja"
                      hint="Recibió efectivo o transferencia bancaria. Se registra ingreso de caja por separado."
                      active={origen === 'CAJA'}
                      onClick={() => setOrigen('CAJA')}
                    />
                    <RadioRow
                      label="Débito de Ahorro corriente"
                      hint="Se debita el monto de la cuenta de ahorro del asociado."
                      active={origen === 'DEBITO_AHORRO_CORRIENTE'}
                      onClick={() => setOrigen('DEBITO_AHORRO_CORRIENTE')}
                    />
                  </Section>

                  <Section title="7. Beneficiarios (opcional)">
                    <BeneficiariosEditor
                      items={beneficiarios}
                      onChange={setBeneficiarios}
                      sumaPorcentajes={sumaPorcentajes}
                    />
                  </Section>

                  {error ? (
                    <div
                      style={{
                        padding: 12,
                        borderRadius: 10,
                        background: 'rgba(163,45,45,0.08)',
                        border: '1px solid rgba(163,45,45,0.22)',
                        fontSize: 12,
                        color: '#A32D2D',
                      }}
                    >
                      {error}
                    </div>
                  ) : null}
                </>
              ) : null}
            </div>
          )}
        </div>

        {/* Footer */}
        {!resultado ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 10,
              padding: '14px 20px',
              borderTop: '1px solid var(--border)',
              background: 'var(--n-50)',
            }}
          >
            <button onClick={onClose} className="btn btn-ghost" disabled={submitting}>
              Cancelar
            </button>
            <button
              onClick={onSubmit}
              disabled={!canSubmit || submitting}
              className="btn btn-primary"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {submitting ? 'Creando…' : 'Crear plazo fijo'}
              {!submitting ? <ArrowRight size={13} weight="bold" /> : null}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--n-500)',
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  )
}

function RadioRow({
  label,
  hint,
  active,
  onClick,
}: {
  label: string
  hint: string
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: 12,
        borderRadius: 10,
        background: active ? 'rgba(240,78,35,0.06)' : 'transparent',
        border: active ? '1px solid rgba(240,78,35,0.30)' : '1px solid var(--border)',
        cursor: 'pointer',
        textAlign: 'left',
        width: '100%',
      }}
    >
      <div
        style={{
          width: 18,
          height: 18,
          borderRadius: 9,
          border: '1.5px solid ' + (active ? 'var(--brand-500)' : 'var(--n-400)'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 2,
        }}
      >
        {active ? (
          <div
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              background: 'var(--brand-500)',
            }}
          />
        ) : null}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--n-900)' }}>
          {label}
        </div>
        <div style={{ fontSize: 11, color: 'var(--n-500)', marginTop: 2, lineHeight: 1.4 }}>
          {hint}
        </div>
      </div>
    </button>
  )
}

function ResultadoCard({
  resultado,
  onDone,
}: {
  resultado: {
    plazo_fijo_id: string
    certificado_no: string
    cuenta_id: string
    capital: number
    fecha_vencimiento: string
    total_neto: number
  }
  onDone: () => void
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 14,
          borderRadius: 12,
          background: 'rgba(184,134,11,0.10)',
        }}
      >
        <Sparkle size={22} weight="duotone" color="#B8860B" />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600 }}>Plazo fijo creado · pendiente de ingreso.</div>
          <div style={{ fontSize: 12, color: 'var(--n-700)', marginTop: 2, lineHeight: 1.5 }}>
            Falta registrar el ingreso del dinero (efectivo, depósito o
            transferencia). Andá a la vista del PF y tocá "Confirmar ingreso"
            cuando lo recibas.
          </div>
        </div>
      </div>

      <table style={{ width: '100%', fontSize: 13 }}>
        <tbody>
          <Row label="Certificado" value={resultado.certificado_no} mono />
          <Row label="Cuenta" value={resultado.cuenta_id} mono />
          <Row label="Capital" value={`Q ${resultado.capital.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
          <Row
            label="Vencimiento"
            value={new Date(
              // DATE 'YYYY-MM-DD': anclar mediodía para no correrse un día en GT (UTC-6)
              resultado.fecha_vencimiento.length === 10
                ? resultado.fecha_vencimiento + 'T12:00:00'
                : resultado.fecha_vencimiento,
            ).toLocaleDateString('es-GT', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
          />
          <Row
            label="Rendimiento estimado"
            value={`+ Q ${resultado.total_neto.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            accent
          />
        </tbody>
      </table>

      <button
        onClick={onDone}
        className="btn btn-primary"
        style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}
      >
        Ir al detalle del plazo fijo
        <ArrowRight size={13} weight="bold" />
      </button>
    </div>
  )
}

function BeneficiariosEditor({
  items,
  onChange,
  sumaPorcentajes,
}: {
  items: Beneficiario[]
  onChange: (next: Beneficiario[]) => void
  sumaPorcentajes: number
}) {
  const update = (idx: number, patch: Partial<Beneficiario>) => {
    onChange(items.map((b, i) => (i === idx ? { ...b, ...patch } : b)))
  }
  const remove = (idx: number) => {
    onChange(items.filter((_, i) => i !== idx))
  }
  const add = () => {
    onChange([...items, { ...BENEFICIARIO_VACIO }])
  }

  if (items.length === 0) {
    return (
      <div
        style={{
          padding: 16,
          borderRadius: 10,
          border: '1px dashed var(--border)',
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 12, color: 'var(--n-500)', marginBottom: 8 }}>
          Sin beneficiarios designados.
        </div>
        <button
          onClick={add}
          className="btn btn-ghost"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--brand-500)',
          }}
        >
          <Plus size={13} weight="bold" />
          Agregar beneficiario
        </button>
      </div>
    )
  }

  const sumaOk = sumaPorcentajes === 100

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((b, idx) => (
        <div
          key={idx}
          style={{
            padding: 12,
            border: '1px solid var(--border)',
            borderRadius: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div style={{ fontSize: 11, color: 'var(--n-500)', fontWeight: 600 }}>
              Beneficiario {idx + 1}
            </div>
            <button
              onClick={() => remove(idx)}
              className="btn btn-ghost"
              style={{ width: 28, height: 28, padding: 0, color: '#A32D2D' }}
              aria-label="Eliminar"
            >
              <Trash size={12} />
            </button>
          </div>
          <input
            value={b.nombre_completo}
            onChange={(e) => update(idx, { nombre_completo: e.target.value })}
            placeholder="Nombre completo"
            style={inputStyle}
          />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <input
              value={b.identificacion}
              onChange={(e) => update(idx, { identificacion: e.target.value })}
              placeholder="DPI / identificación"
              style={inputStyle}
            />
            <input
              value={b.parentesco}
              onChange={(e) => update(idx, { parentesco: e.target.value })}
              placeholder="Parentesco"
              style={inputStyle}
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="number"
              value={b.porcentaje || ''}
              onChange={(e) =>
                update(idx, { porcentaje: parseFloat(e.target.value) || 0 })
              }
              placeholder="0"
              min={0}
              max={100}
              style={{ ...inputStyle, width: 90, textAlign: 'right' }}
            />
            <span style={{ fontSize: 13, color: 'var(--n-700)', fontWeight: 500 }}>% del PF</span>
          </div>
        </div>
      ))}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          borderRadius: 8,
          background: sumaOk ? 'rgba(15,138,87,0.08)' : 'rgba(184,134,11,0.08)',
          fontSize: 12,
          color: sumaOk ? '#0F8A57' : '#B8860B',
          fontWeight: 600,
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {!sumaOk ? <Warning size={13} weight="fill" /> : null}
          Suma total: {sumaPorcentajes}%
        </span>
        {!sumaOk ? (
          <span style={{ fontSize: 11, fontWeight: 500 }}>
            Debe sumar 100% para guardar
          </span>
        ) : null}
      </div>
      <button
        onClick={add}
        className="btn btn-ghost"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          alignSelf: 'flex-start',
          fontSize: 12,
          color: 'var(--brand-500)',
        }}
      >
        <Plus size={12} weight="bold" />
        Agregar otro
      </button>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  fontSize: 13,
  fontFamily: 'inherit',
}

function Row({
  label,
  value,
  mono,
  accent,
}: {
  label: string
  value: string
  mono?: boolean
  accent?: boolean
}) {
  return (
    <tr>
      <td style={{ color: 'var(--n-500)', padding: '6px 0', fontSize: 12 }}>{label}</td>
      <td
        style={{
          textAlign: 'right',
          padding: '6px 0',
          fontFamily: mono ? 'ui-monospace, Menlo, monospace' : undefined,
          fontWeight: accent ? 700 : 500,
          color: accent ? '#0F8A57' : 'var(--n-900)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </td>
    </tr>
  )
}
